import { AUTH_CHANNEL, AUTH_LOGOUT_MESSAGE } from './authConfig'
import { clearAuthCookies, msalInstance } from './msal'

/**
 * Identidad de este documento, para reconocer el propio aviso de logout.
 *
 * BroadcastChannel excluye del reparto al objeto emisor, pero NO al contexto: el canal de larga
 * vida que registra `MsalCustomProvider` en esta misma pestaña también recibe el mensaje. Sin este
 * discriminador, la pestaña que cerraba sesión atendía su propio aviso, hacía `clearCache()` y
 * navegaba a /autenticacion compitiendo con el `logoutRedirect` a Entra que seguía en vuelo. Si
 * ganaba la navegación local —lo probable, porque `clearCache()` es inmediato y `logoutRedirect`
 * tiene que esperar initialize, armar el request y limpiar su propio cache— el navegador nunca
 * llegaba al endpoint de end-session: la sesión de Entra quedaba viva y el siguiente acceso volvía
 * a entrar solo. Es justo el fallo que el comentario de `cerrarSesion` dice querer evitar.
 *
 * Va como marca en el mensaje y no como bandera de módulo a propósito: una bandera hay que apagarla
 * en algún momento, y el reparto es asíncrono, así que apagarla siempre reabre la ventana para un
 * mensaje que todavía venía en camino. La marca no depende de cuándo llegue.
 *
 * No es un secreto ni se usa para autorizar nada: solo distingue documentos.
 */
const ID_DE_ESTA_PESTANA = `${Date.now()}-${Math.random().toString(36).slice(2)}`

interface AvisoDeLogout {
  tipo: typeof AUTH_LOGOUT_MESSAGE
  origen: string
}

const publicarLogout = (): void => {
  if (typeof BroadcastChannel === 'undefined') {
    return
  }

  const aviso: AvisoDeLogout = { tipo: AUTH_LOGOUT_MESSAGE, origen: ID_DE_ESTA_PESTANA }

  // Sin close(): cerrar el canal en el mismo tick puede descartar el mensaje todavía en cola,
  // y acá se navega a Entra enseguida. El canal muere con el documento.
  new BroadcastChannel(AUTH_CHANNEL).postMessage(aviso)
}

const comoAviso = (data: unknown): Partial<AvisoDeLogout> | null =>
  typeof data === 'object' && data !== null ? (data as Partial<AvisoDeLogout>) : null

/**
 * true solo para un logout publicado por OTRA pestaña.
 *
 * Acepta además el string suelto que publicaban las versiones anteriores: durante un despliegue
 * pueden convivir pestañas viejas y nuevas, y el aviso de una pestaña vieja debe seguir valiendo.
 */
const esLogoutDeOtraPestana = (data: unknown): boolean => {
  if (data === AUTH_LOGOUT_MESSAGE) {
    return true
  }

  const aviso = comoAviso(data)

  return aviso?.tipo === AUTH_LOGOUT_MESSAGE && aviso.origen !== ID_DE_ESTA_PESTANA
}

/**
 * Suscribe a la pestaña al logout iniciado en otra pestaña del mismo origen.
 * Devuelve la función de limpieza. Quien inicia el logout es quien mata la sesión de
 * Entra; las demás solo purgan su cache y salen de la ruta privada.
 */
export const escucharLogoutDeOtrasPestanas = (alCerrarSesion: () => void): (() => void) => {
  if (typeof BroadcastChannel === 'undefined') {
    return () => undefined
  }

  const canal = new BroadcastChannel(AUTH_CHANNEL)

  canal.onmessage = (evento: MessageEvent): void => {
    if (esLogoutDeOtraPestana(evento.data)) {
      alCerrarSesion()
    }
  }

  return () => canal.close()
}

/**
 * Cierre de sesión completo y en el orden correcto:
 * cookies del proxy → aviso a las otras pestañas → sesión de Entra.
 *
 * Si las cookies no se borran ANTES de volver del logout de Entra, el proxy ve un
 * access_token todavía válido y redirige a HOME: el usuario "cierra sesión" y vuelve a
 * entrar solo. Y si solo se limpiara el cache local (clearCache/removeAccount), la sesión
 * de Entra seguiría viva y el primer ssoSilent volvería a emitir token: por eso logoutRedirect.
 */
export const cerrarSesion = async (): Promise<void> => {
  clearAuthCookies()
  publicarLogout()

  const account = msalInstance.getActiveAccount() ?? undefined

  if (window.self !== window.top) {
    await msalInstance.logoutPopup({ account })

    return
  }

  await msalInstance.logoutRedirect({ account, postLogoutRedirectUri: '/' })
}
